import React from "react";
import "./styles.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFloppyDisk, faXmark, faUserSlash, faUserCheck } from "@fortawesome/free-solid-svg-icons";
import FloatingActions, { FloatingAction } from "../FloatingActions";
import Card from "../Card";
import axios from "axios";
import { User } from "../UserSettings/types";
import UserStock, { UserStockItem } from "../UserStock";
import useAppData from "@/store/useAppData";

export interface appUserCardProps {
    userId?: string;
    onSave?: (userId: string) => void;
}

const AppUserCard: React.FC<appUserCardProps> = ({ userId, onSave }) => {

    const selectedModule = useAppData(state => state.selectedModule);
    const [name, setName] = React.useState<string | null>(null);
    const [email, setEmail] = React.useState<string | null>(null);
    const [login, setLogin] = React.useState<string | null>(null);
    const [password, setPassword] = React.useState<string | null>(null);
    const [changePassword, setChangePassword] = React.useState<boolean>(false);
    const [role, setRole] = React.useState<string>("edit");
    const [userStocks, setUserStocks] = React.useState<UserStockItem[]>([]);
    const [error, setError] = React.useState<string | null>(null);
    const [active, setActive] = React.useState<boolean | null>(null);
    const [changingActive, setChangingActive] = React.useState(false);

    React.useEffect(() => {
        setError(null);

        if (userId) {
            loadUser(userId);
        }

        loadUserStocks(userId);
    }, []);

    const setUserData = (user: User) => {
        setName(user.name);
        setEmail(user.email);
        setLogin(user.login);
        //setRole(user.userrole);
        setChangePassword(user.changePassword ?? false);
        setActive(user.active);

        setRolesByDBData(user.userrole);
    }

    const setRolesByDBData = (rolesDB: string) => {
        const selectedRoles = (rolesDB ?? "")
            .split("|")
            .map(role => role.trim())
            .filter(Boolean);

        if (selectedRoles?.length > 0) {
            setRole(selectedRoles[0]);
        } else {
            setRole(undefined);
        }
    };

    const loadUser = async (userId: string) => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/appuser/get",
                    data: {
                        userId: userId
                    }
                })
            );

            if (res.data.user.length > 0) {
                const user: User = res.data.user[0];
                setUserData(user);
            }
        } catch (err) {
            throw err;
        }
    }

    const loadUserStocks = async (userId: string) => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/userstock/getUserStocks",
                    data: {
                        userId: userId
                    }
                })
            );

            if (res.data.stocks.length > 0) {
                setUserStocks(res.data.stocks);
            }
        } catch (err) {
            throw err;
        }
    }

    const deleteUserStock = async (userId: string) => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/userstock/deleteUserStocks",
                    data: {
                        userId: userId
                    }
                })
            );

        } catch (err) {
            throw err;
        }
    }

    const insertUserStock = async (userId: string, stockId: string) => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/userstock/insertUserStock",
                    data: {
                        userId: userId,
                        stockId: stockId
                    }
                })
            );

            return res.data.id;
        } catch (err) {
            throw err;
        }
    }

    const updateUserStocks = async (userId: string, userStocks: UserStockItem[]) => {
        await deleteUserStock(userId);
        const selectedUserStocks = userStocks.filter(userStock => userStock.selected);

        for (const userstock of selectedUserStocks) {
            await insertUserStock(userId, userstock.id);
        }
    }

    const insertUser = async (name: string, email: string, login: string, password: string, userrole: string, changePassword) => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/appuser/insert",
                    data: {
                        name: name,
                        login: login,
                        email: email,
                        password: password,
                        userrole: userrole,
                        changePassword: changePassword
                    }
                })
            );

            return res.data.id;
        } catch (err) {
            throw err;
        }
    }

    const updateUser = async (idUser: string, name: string, email: string, login: string, password: string, userrole: string, changePaswword: boolean) => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/appuser/update",
                    data: {
                        id: idUser,
                        name: name,
                        login: login,
                        email: email,
                        password: password,
                        userrole: userrole,
                        changePassword: changePassword
                    }
                })
            );
        } catch (err) {
            throw err;
        }
    }

    const validateNewUser = () => {
        const fn = name?.trim();
        const ln = login?.trim();
        const pn = password?.trim();

        if (!fn || !ln || (!pn && !userId)) {
            return "Vyplňte jméno, login, heslo.";
        }

        return null;
    };

    const onCreate = async () => {
        setError(null);

        const validationError = validateNewUser();
        if (validationError) {
            setError(validationError);
            return;
        }

        if (userId) {
            await updateUser(userId, name, email, login, password, role, changePassword);
            await updateUserStocks(userId, userStocks);
            onSave(userId);
        } else {
            const newUserId = await insertUser(name, email, login, password, role, changePassword);
            await updateUserStocks(newUserId, userStocks);
            onSave(newUserId);
        }
    };

    const onSelectionChangedUserStocks = (userStockId: string) => {
        setUserStocks(prev =>
            prev.map(item =>
                item.id === userStockId
                    ? { ...item, selected: !item.selected }
                    : item
            )
        );
    };

    const onToggleActive = async () => {
        if (!userId || active == null || changingActive) return;
        setError(null);
        setChangingActive(true);
        try {
            await axios.post(active ? "api/appuser/deactivate" : "api/appuser/activate", { id: userId });
            setActive(!active);
            await onSave?.(userId);
        } catch {
            setError("Aktivní stav uživatele se nepodařilo změnit. Zkuste to prosím znovu.");
        } finally {
            setChangingActive(false);
        }
    };

    const actions: FloatingAction[] = [
        ...(userId && active != null && !changingActive ? [{
            key: "toggleActive",
            label: active ? "Deaktivovat" : "Aktivovat",
            icon: <FontAwesomeIcon icon={active ? faUserSlash : faUserCheck} />,
            onClick: onToggleActive,
        }] : []),
        {
            key: "cancel",
            label: "Zrušit",
            icon: <FontAwesomeIcon icon={faXmark} />,
            onClick: () => onSave(null),
        },
        {
            key: "save",
            label: "Uložit",
            icon: <FontAwesomeIcon icon={faFloppyDisk} />,
            onClick: onCreate,
        },
    ];

    return (
        <Card title="Uživatel" suppressInit={true} displaySelectedStock={false}>
            <div className="nec-stack">
                <>
                    <div className="nec-block">
                        <div className="nec-header">Jméno</div>
                        <div className="nec-control">
                            <input
                                className="nec-input"
                                value={name}
                                required
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Jméno..."
                            />
                        </div>
                    </div>


                    <div className="nec-block">
                        <div className="nec-header">Login</div>
                        <div className="nec-control">
                            <input
                                className="nec-input"
                                value={login}
                                required
                                onChange={(e) => setLogin(e.target.value)}
                                placeholder="Login..."
                            />
                        </div>
                    </div>

                    <div className="nec-block">
                        <div className="nec-header">Email</div>
                        <div className="nec-control">
                            <input
                                className="nec-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Email..."
                            />
                        </div>
                    </div>

                    <div className="nec-block">
                        <div className="nec-header">Heslo</div>
                        <div className="nec-control">
                            <input
                                className="nec-input"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Heslo..."
                            />
                        </div>
                    </div>

                    <div className="nec-block">
                        <div className="nec-header">Změnit heslo po přihlášení</div>
                        <div className="nec-control">
                            <input
                                className="nec-input"
                                type="checkbox"
                                checked={changePassword}
                                onChange={(e) => setChangePassword(e.target.checked)}
                                placeholder="Heslo..."
                            />
                        </div>
                    </div>

                    <div className="nec-block">
                        <div className="nec-header">Role</div>
                        <div className="nec-control">
                            <select
                                className="stock-item-control1"
                                value={role}
                                onChange={(e) => { setRole(e.target.value); }}
                            >
                                <option key={"viewer"} value={"viewer"}>
                                    Prohlížení
                                </option>
                                <option key={"edit"} value={"edit"}>
                                    Editace
                                </option>
                                {/* <option key={"operator"} value={"operator"}>
                                    Operátor
                                </option>
                                <option key={"kontrola"} value={"kontrola"}>
                                    Kontrola
                                </option> */}
                                <option key={"admin"} value={"admin"}>
                                    Administrátor
                                </option>
                            </select>
                        </div>
                    </div>

                    <div className="nec-block">
                        <div className="nec-header">Sklad</div>
                        <div className="nec-control">
                            <UserStock items={userStocks} onSelectionChanged={onSelectionChangedUserStocks}></UserStock>
                        </div>
                    </div>
                </>

                {error ? <div className="form-error">{error}</div> : null}
            </div>

            {actions?.length ? (
                <div className="item-list-actions">
                    <FloatingActions actions={actions} />
                </div>
            ) : null}
        </Card>
    );

};

export default AppUserCard;
